import { useFormContext, useWatch, useFieldArray } from "react-hook-form";
import { useEffect, useState } from "react";
import moment from "moment";
import states from "../../data/states";
import countries from "../../data/countries";

export default function BillingDetails({
  isUpdateMode, inputClasses, labelClasses, isQuote,
}) {
  const [minBillingStart, setMinBillingStart] = useState(null);
  const [billingStartLocked, setBillingStartLocked] = useState(false);

  const {
    register,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "billing.contacts",
  });

  const billing = useWatch({ name: "billing" });

  useEffect(() => {
    if (billing?.interval && billing?.intervalCount !== undefined) {
      const correctOption = mapIntervalToIntervalOption(billing.interval, billing.intervalCount);
      if (billing.intervalOption !== correctOption) {
        setValue("billing.intervalOption", correctOption, { shouldDirty: false });
      }
    }
  }, [billing?.interval, billing?.intervalCount]);

  useEffect(() => {
    if (isUpdateMode) return;

    if (billing.trial && billing.trial.enabled) {
      setBillingStartLocked(false);
      setMinBillingStart(null);
      return;
    }

    const sub = billing?.subscriptionStartOn;
    const today = moment().startOf("day");
    if (!sub) {
      setBillingStartLocked(false);
      setMinBillingStart(null);
      return;
    }

    const subDate = moment(sub, "YYYY-MM-DD").startOf("day");
    if (subDate.isSameOrAfter(today)) {
      setBillingStartLocked(true);
      setMinBillingStart(null);
      const formatted = subDate.format("YYYY-MM-DD");
      if (billing?.cycleAnchorOn !== formatted) {
        setValue("billing.cycleAnchorOn", formatted, { shouldDirty: true, shouldValidate: true });
      }
    } else {
      setBillingStartLocked(false);
      const minDateStr = today.format("YYYY-MM-DD");
      setMinBillingStart(minDateStr);
      if (billing?.cycleAnchorOn) {
        const cycleDate = moment(billing.cycleAnchorOn, "YYYY-MM-DD").startOf("day");
        if (cycleDate.isBefore(today)) {
          setValue("billing.cycleAnchorOn", minDateStr, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    }
  }, [billing?.subscriptionStartOn, isUpdateMode, billing.trial]);

  useEffect(() => {
    const today = moment().startOf("day");
    const invoiceDate = moment(billing?.invoiceDueOn, "YYYY-MM-DD").startOf("day");
    if (
      billing?.invoiceDueOn
      && !billing.invoiceDueOn.startsWith("0")
      && invoiceDate.isBefore(today)
    ) {
      setValue("billing.invoiceDueOn", today.toISOString().slice(0, 10), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    const trialDate = moment(billing?.trial?.expiresOn, "YYYY-MM-DD").startOf("day");
    if (
      billing?.trial?.expiresOn
      && !billing.trial.expiresOn.startsWith("0")
      && trialDate.isBefore(today)
    ) {
      setValue("billing.trial.expiresOn", today.toISOString().slice(0, 10), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [billing?.invoiceDueOn, billing?.trialEndsOn]);

  const mapIntervalOptionToInterval = (opt) => {
    switch (opt) {
      case "monthly":
        return { interval: "month", intervalCount: 1 };
      case "quarterly":
        return { interval: "month", intervalCount: 3 };
      case "semiAnnually":
        return { interval: "month", intervalCount: 6 };
      case "yearly":
        return { interval: "year", intervalCount: 1 };
      case "2year":
        return { interval: "year", intervalCount: 2 };
      case "3year":
        return { interval: "year", intervalCount: 3 };
      default:
        return { interval: "year", intervalCount: 1 };
    }
  };

  const mapIntervalToIntervalOption = (interval, intervalCount) => {
    if (interval === "month" && intervalCount === 1) return "monthly";
    if (interval === "month" && intervalCount === 3) return "quarterly";
    if (interval === "month" && intervalCount === 6) return "semiAnnually";
    if (interval === "year" && intervalCount === 1) return "yearly";
    if (interval === "year" && intervalCount === 2) return "2year";
    if (interval === "year" && intervalCount === 3) return "3year";
    return "yearly"; // default
  };

  const handleIntervalOptionChange = (opt) => {
    const { interval, intervalCount } = mapIntervalOptionToInterval(opt);
    setValue("billing.intervalOption", opt, { shouldDirty: true });
    setValue("billing.interval", interval, { shouldDirty: true });
    setValue("billing.intervalCount", intervalCount, { shouldDirty: true });
  };

  const handleSelectCountry = (event) => {
    const val = event.target.value;
    setValue("billing.address.country", val, { shouldDirty: true, shouldValidate: true });
    if (val !== "US" && val !== "USA") {
      setValue("billing.address.state", "", { shouldDirty: true, shouldValidate: true });
    }

    setValue("billing.address.zipcode", billing.address.zipcode, { shouldValidate: true });
  };

  const handleSelectState = (event) => {
    setValue("billing.address.state", event.target.value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <div>
      <div className="flex flex-col gap-2 w-1/2">
        {!isUpdateMode && !isQuote && (
          <div className="flex items-center">
            <input
              {...register("billing.trial.enabled")}
              id="billing.trial-enabled"
              type="checkbox"
              checked={billing.trial?.enabled}
              onChange={(e) => setValue("billing.trial.enabled", e.target.checked, { shouldDirty: true })
              }
              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <label
              htmlFor="billing.trial-enabled"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              Enable trial (skip sub creation)
            </label>
          </div>
        )}

        {billing.trial?.enabled && !isQuote && (
          <div>
            <label htmlFor="billing.trial-expires-on" className={labelClasses}>
              Trial Expires On
            </label>
            <input
              {...register("billing.trial.expiresOn")}
              id="billing.trial-expires-on"
              type="date"
              value={billing.trial.expiresOn}
              onChange={(e) => setValue("billing.trial.expiresOn", e.target.value, { shouldDirty: true })
              }
              className={inputClasses}
            />
          </div>
        )}
      </div>

      <div className="text-xs text-gray-500 my-4" id="billing-frequency-help">
        {billing.trial?.enabled ? (
          <span>Billing frequency & scheduling options will be ignored while trial is active.</span>
        ) : (
          <span>Set billing frequency and dates below.</span>
        )}
      </div>

      {!billing.trial?.enabled && (
        <div className={`grid grid-cols-${isUpdateMode ? "2" : "3"} gap-5`}>
          <div className="flex flex-col gap-2">
            <label htmlFor="billing-interval-option" className={labelClasses}>
              Billing frequency
            </label>
            <select
              {...register("billing.intervalOption")}
              id="billing-interval-option"
              value={billing.intervalOption}
              onChange={(e) => handleIntervalOptionChange(e.target.value)}
              className={inputClasses}
              disabled={billing.trial?.enabled}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly (3 months)</option>
              <option value="semiAnnually">Semi-annually (6 months)</option>
              <option value="yearly">Yearly</option>
              <option value="2year">2-year</option>
              <option value="3year">3-year</option>
            </select>

            {(!isQuote || !billing.chargeAutomatically) && (
              <div className="flex flex-col">
                <label htmlFor="billing-invoice-due-on" className={labelClasses}>
                  Invoice due on
                </label>
                <div className="flex flex-row items-center gap-2">
                  <input
                    {...register("billing.invoiceDueOn")}
                    id="billing-invoice-due-on"
                    type="date"
                    value={billing.invoiceDueOn}
                    onChange={(e) => setValue("billing.invoiceDueOn", e.target.value, { shouldDirty: true })
                    }
                    className={inputClasses}
                    disabled={billing.chargeAutomatically || billing.trial?.enabled}
                  />
                  <select
                    id="billing-invoice-net-term"
                    className={`${inputClasses} w-32`}
                    onChange={(e) => {
                      const days = parseInt(e.target.value, 10);

                      const newDueDate = moment().add(days, "days").format("YYYY-MM-DD"); // local date

                      setValue("billing.invoiceDueOn", newDueDate, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    disabled={billing.chargeAutomatically || billing.trial?.enabled}
                  >
                    <option value="0">Set Net Term</option>
                    <option value="15">Net 15</option>
                    <option value="30">Net 30</option>
                    <option value="45">Net 45</option>
                    <option value="60">Net 60</option>
                    <option value="90">Net 90</option>
                    <option value="120">Net 120</option>
                    <option value="180">Net 180</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {!isUpdateMode && !isQuote && (
            <div>
              <div>
                <label htmlFor="subscription-start-on" className={labelClasses}>
                  Subscription Start Date
                </label>
                <input
                  {...register("billing.subscriptionStartOn")}
                  id="subscription-start-on"
                  type="date"
                  value={billing.subscriptionStartOn}
                  onChange={(e) => setValue("billing.subscriptionStartOn", e.target.value, { shouldDirty: true })
                  }
                  className={inputClasses}
                  disabled={billing.trial?.enabled}
                />
              </div>
              <div>
                <label htmlFor="billing-cycle-anchor" className={labelClasses}>
                  Billing Start Date
                </label>
                <input
                  {...register("billing.cycleAnchorOn")}
                  id="billing-cycle-anchor"
                  type="date"
                  value={billing.cycleAnchorOn}
                  onChange={(e) => setValue("billing.cycleAnchorOn", e.target.value, { shouldDirty: true })
                  }
                  className={inputClasses}
                  disabled={billingStartLocked || billing.trial?.enabled}
                  min={minBillingStart || undefined}
                />
                {billingStartLocked && !billing.trial?.enabled && (
                  <p className="text-xs text-gray-500 mt-1">Locked to subscription start date</p>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {!isQuote && (
              <>
                <label className={labelClasses}>Billing Options</label>
                <div className="flex items-end pb-2">
                  <input
                    {...register("billing.chargeAutomatically")}
                    id="billing-charge-automatically"
                    type="checkbox"
                    checked={billing.chargeAutomatically}
                    onChange={(e) => setValue("billing.chargeAutomatically", e.target.checked, {
                      shouldDirty: true,
                    })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    disabled={billing.trial?.enabled}
                  />
                  <label
                    htmlFor="billing-charge-automatically"
                    className="ml-2 text-sm font-medium text-gray-700"
                  >
                    Charge automatically
                  </label>
                </div>

                <div className="flex items-end pb-2">
                  <input
                    {...register("billing.enableTaxExemption")}
                    id="billing-enable-tax-exemption"
                    type="checkbox"
                    checked={billing.enableTaxExemption}
                    onChange={(e) => setValue("billing.enableTaxExemption", e.target.checked, {
                      shouldDirty: true,
                    })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    disabled={billing.trial?.enabled}
                  />
                  <label
                    htmlFor="billing-enable-tax-exemption"
                    className="ml-2 text-sm font-medium text-gray-700"
                  >
                    Enable tax exemption
                  </label>
                </div>
              </>
            )}

            {isUpdateMode && !isQuote && (
              <div className="flex items-center">
                <input
                  {...register("billing.resetCycle")}
                  id="billing-reset-cycle"
                  type="checkbox"
                  checked={billing.resetCycle}
                  onChange={(e) => setValue("billing.resetCycle", e.target.checked, { shouldDirty: true })
                  }
                  className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  disabled={billing.trial?.enabled}
                />
                <label
                  htmlFor="billing-reset-cycle"
                  className="ml-2 text-sm font-medium text-gray-700"
                >
                  Reset billing cycle
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {!isQuote && (
        <div className="pt-4 border-t border-gray-200">
          <label htmlFor="billing-address-line" className={labelClasses}>
            Billing address
          </label>
          <div className="grid grid-cols-1 md: grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <input
                {...register("billing.address.line")}
                id="billing-address-line"
                type="text"
                placeholder="Street address"
                value={billing.address.line}
                onChange={(e) => setValue("billing.address.line", e.target.value, { shouldDirty: true })
                }
                className={inputClasses}
              />
            </div>
            <div className="md:col-span-2">
              <input
                {...register("billing.address.line2")}
                id="billing-address-line2"
                type="text"
                placeholder="Apartment, suite, unit, building, floor, etc. (optional)"
                value={billing.address.line2}
                onChange={(e) => setValue("billing.address.line2", e.target.value, { shouldDirty: true })
                }
                className={inputClasses}
              />
            </div>

            <input
              {...register("billing.address.city")}
              id="billing-address-city"
              type="text"
              placeholder="City"
              value={billing.address.city}
              onChange={(e) => setValue("billing.address.city", e.target.value, { shouldDirty: true })
              }
              className={inputClasses}
            />

            {billing.address.country === "US" || billing.address.country === "USA" ? (
              <div>
                <select
                  {...register("billing.address.state")}
                  id="billing-address-state"
                  value={billing.address.state}
                  onChange={handleSelectState}
                  className={inputClasses}
                >
                  <option value="">Choose a state</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <input
                {...register("billing.address.state")}
                id="billing-address-state"
                type="text"
                placeholder="State/Province"
                value={billing.address.state}
                onChange={(e) => setValue("billing.address.state", e.target.value, { shouldDirty: true })
                }
                className={inputClasses}
              />
            )}

            <div>
              <input
                {...register("billing.address.zipcode", {
                  validate: (value) => {
                    const { country } = billing.address;
                    if ((country === "US" || country === "USA") && value.length !== 5) {
                      return "US zip code must be 5 characters";
                    }
                    return true;
                  },
                })}
                id="billing-address-zip"
                type="text"
                placeholder="Zip code"
                value={billing.address.zipcode}
                onChange={(e) => setValue("billing.address.zipcode", e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
                }
                className={inputClasses}
              />
              {errors?.billing?.address?.zipcode && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.billing.address.zipcode.message}
                </p>
              )}
            </div>

            <div>
              <select
                {...register("billing.address.country")}
                id="billing-address-country"
                value={billing.address.country}
                onChange={handleSelectCountry}
                className={inputClasses}
                required
              >
                {countries.map((c) => (
                  <option key={c.id} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="pt-4">
        <label htmlFor="billing-custom-memo" className={labelClasses}>
          Custom memo
        </label>
        <input
          {...register("billing.customMemo")}
          id="billing-custom-memo"
          type="text"
          value={billing.customMemo}
          onChange={(e) => setValue("billing.customMemo", e.target.value, { shouldDirty: true })}
          placeholder="e.g.Quarterly subscription renewal for Q4"
          className={inputClasses}
        />
      </div>

      <div className="pt-4">
        <label className={labelClasses}>Billing Contacts</label>
        <p className="text-xs text-gray-500 mt-1">
          Provide name and email for each billing contact. Add multiple contacts as needed.
        </p>

        <div className="space-y-3 mt-3">
          {fields.map((field, index) => (
            <div key={field.id} className="flex gap-2 items-center">
              <input
                {...register(`billing.contacts.${index}.name`)}
                type="text"
                placeholder="Name (optional)"
                defaultValue={field.name}
                className={`${inputClasses} w-1/2`}
                onChange={(e) => setValue(`billing.contacts.${index}.name`, e.target.value, { shouldDirty: true })
                }
              />
              <input
                {...register(`billing.contacts.${index}.email`, {
                  validate: (value) => {
                    if (!value) return "Email is required";
                    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return re.test(String(value).toLowerCase()) || "Invalid email";
                  },
                })}
                type="email"
                placeholder="email@example.com"
                defaultValue={field.email}
                className={`${inputClasses} w-1/2`}
                onChange={(e) => setValue(`billing.contacts.${index}.email`, e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
                }
              />
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="text-sm text-red-600 ml-2"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div>
            <button
              type="button"
              onClick={() => append({ name: "", email: "" })}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              + Add contact
            </button>
          </div>

          {errors?.billing?.contacts && (
            <p className="text-xs text-red-600 mt-1">{errors.billing.contacts.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}
